import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { ORDER_STATUS_LABELS_LAO } from "@/modules/orders/domain/types";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const FILTER_ALL = "__all__" as const;

function toParam(v: string): string | undefined {
  return v === FILTER_ALL || !v ? undefined : v;
}

type OrdersSearch = {
  status?: string;
  branchId?: string;
  channel?: string;
  createdFrom?: string;
  createdTo?: string;
  pickupFrom?: string;
  pickupTo?: string;
  search?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function OrdersFilter() {
  const nav = useNavigate({ from: "/app/orders" });
  const search = useSearch({ from: "/app/orders" }) as OrdersSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });

  const branches: BranchRow[] = branchesData?.data ?? [];
  const branchOptions = [
    { value: FILTER_ALL, label: "ທຸກສາຂາ" },
    ...branches.map((b) => ({ value: b.id, label: b.name })),
  ];
  const channelOptions = [
    { value: FILTER_ALL, label: "ທຸກຊ່ອງທາງ" },
    { value: "APP", label: "APP" },
    { value: "WALKIN", label: "WALKIN" },
  ];

  const hasFilter =
    !!search.status ||
    !!search.branchId ||
    !!search.channel ||
    !!search.createdFrom ||
    !!search.createdTo ||
    !!search.pickupFrom ||
    !!search.pickupTo ||
    !!search.search;

  const [searchInputValue, setSearchInputValue] = useState<string>(
    search.search ?? "",
  );
  useEffect(() => {
    setSearchInputValue(search.search ?? "");
  }, [search.search]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    update({ search: val || undefined });
  }, 400);

  const update = (next: Partial<OrdersSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  const clearFilters = () => {
    setSearchInputValue("");
    nav({
      search: {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sortField: search.sortField,
        sortOrder: search.sortOrder,
      },
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 pb-2">
      <Tabs
        className="w-full"
        value={search.status ?? FILTER_ALL}
        onValueChange={(v) => update({ status: toParam(v ?? "") })}
      >
        <TabsList className="h-9 flex-wrap gap-1 bg-muted/50">
          <TabsTrigger value={FILTER_ALL} className="text-xs">
            ທຸກສະຖານະ
          </TabsTrigger>
          <TabsTrigger value="DRAFT" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.DRAFT}
          </TabsTrigger>
          <TabsTrigger value="CONFIRMED" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.CONFIRMED}
          </TabsTrigger>
          <TabsTrigger value="PREPARING" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.PREPARING}
          </TabsTrigger>
          <TabsTrigger value="READY" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.READY}
          </TabsTrigger>
          <TabsTrigger value="COMPLETED" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.COMPLETED}
          </TabsTrigger>
          <TabsTrigger value="CANCELLED" className="text-xs">
            {ORDER_STATUS_LABELS_LAO.CANCELLED}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Input
        type="search"
        placeholder="ຄົ້ນຫາ order_no / ລູກຄ້າ"
        className="w-48"
        defaultValue={searchInputValue}
        onChange={(e) => {
          debouncedSearch(e.target.value);
        }}
      />

      <SimpleSelect
        placeholder="ສາຂາ"
        value={search.branchId ?? FILTER_ALL}
        onValueChange={(v) => update({ branchId: toParam(v ?? "") })}
        options={branchOptions}
        className="w-40"
      />
      <SimpleSelect
        placeholder="ຊ່ອງທາງ"
        value={search.channel ?? FILTER_ALL}
        onValueChange={(v) => update({ channel: toParam(v ?? "") })}
        options={channelOptions}
        className="w-32"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.createdFrom ? parseISO(search.createdFrom) : undefined,
          to: search.createdTo ? parseISO(search.createdTo) : undefined,
        }}
        onChange={(range) => {
          const from = Array.isArray(range)
            ? range[0]
            : (range as { from?: Date; to?: Date })?.from;
          const to = Array.isArray(range)
            ? range[1]
            : (range as { from?: Date; to?: Date })?.to;
          update({
            createdFrom: from ? formatDateForInput(from) : undefined,
            createdTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນທີສ້າງ"
        className="h-9 w-fit min-w-56"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.pickupFrom ? parseISO(search.pickupFrom) : undefined,
          to: search.pickupTo ? parseISO(search.pickupTo) : undefined,
        }}
        onChange={(range) => {
          const from = Array.isArray(range)
            ? range[0]
            : (range as { from?: Date; to?: Date })?.from;
          const to = Array.isArray(range)
            ? range[1]
            : (range as { from?: Date; to?: Date })?.to;
          update({
            pickupFrom: from ? formatDateForInput(from) : undefined,
            pickupTo: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ວັນນັບຮັບ"
        className="h-9 w-fit min-w-56"
      />

      {hasFilter && (
        <button
          type="button"
          onClick={clearFilters}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      )}
    </div>
  );
}
